import {
  TransactionDetail,
  TransactionStatus,
} from "@/type/core/transaction/transaction.types";
import DetailItem from "@components/core/items/DetailItem";
import BasicProgress from "@components/core/progress/BasicProgress";
import AlertIcon from "@static/core/icons/icon-alert.svg?react";
import CheckIcon from "@static/core/icons/icon-check.svg?react";
import ChevronCircleIcon from "@static/core/icons/icon-chevron-circle.svg?react";
import CrossIcon from "@static/core/icons/icon-cross.svg?react";
import DoubleChevronIcon from "@static/core/icons/icon-double-chevron-circle.svg?react";
import FolderIcon from "@static/core/icons/icon-folder.svg?react";
import { History } from "@type/core/dashboard/dashboard.types";
import DateFormat from "@utils/DateFormat";
import { formatName } from "@utils/helpers/format-helper";
import { Col, Row } from "antd";
import parse from "html-react-parser";
import i18n from "i18next";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

const showProgressionList: TransactionStatus[] = [
  "prepared",
  "draft",
  "pending",
];

function getBackgroundColor(
  status: TransactionStatus | undefined,
  classified: boolean = false,
) {
  if (classified) {
    return " bg-secondary";
  }
  switch (status) {
    case "prepared":
    case "draft":
    case "expired":
      return " bg-secondary";
    case "pending":
    case "finished":
      return " bg-success";
    case "refused":
    case "canceled":
      return " bg-danger";
    case "inProgress":
      return " bg-warning";
    default:
      return "";
  }
}

function getTextColor(
  status: TransactionStatus | undefined,
  classified: boolean = false,
) {
  if (classified) {
    return " text-secondary";
  }
  switch (status) {
    case "prepared":
    case "draft":
    case "expired":
      return " text-secondary";
    case "finished":
    case "pending":
      return " text-success";
    case "refused":
    case "canceled":
      return " text-danger";
    case "inProgress":
      return " text-warning";
    default:
      return "";
  }
}

function getIcon(status: TransactionStatus | "", classified: boolean = false) {
  if (classified) {
    return <FolderIcon className="icon-30" />;
  }
  switch (status) {
    case "prepared":
      return <ChevronCircleIcon className="icon-30" />;
    case "draft":
      return <ChevronCircleIcon className="icon-30" />;
    case "finished":
      return <CheckIcon className="icon-30" />;
    case "refused":
      return <CrossIcon className="icon-30" />;
    case "canceled":
      return <AlertIcon className="icon-30" />;
    case "inProgress":
    case "pending":
      return <DoubleChevronIcon className="icon-30" />;
    default:
      return undefined;
  }
}

function historyToMessage(
  history: History,
  displayAuthor: boolean = true,
  getUser: boolean = true,
  errorHistories: History[] = [],
  historyExpired: History[] = [],
) {
  return history
    ? dateToMessage(history.date) +
        authorToMessage(history, displayAuthor, getUser) +
        errorHistories
          .map(
            (errorHistory) =>
              "&#10;Relancé : " +
              dateToMessage(errorHistory.date) +
              authorToMessage(errorHistory, displayAuthor, getUser),
          )
          .join("") +
        historyExpired
          .map(
            (historyEx) =>
              "&#10;Relancé : " +
              dateToMessage(historyEx.date) +
              authorToMessage(historyEx, displayAuthor, getUser),
          )
          .join("")
    : "-";
}

export function dateToMessage(date: string): string {
  return DateFormat.fullDate(new Date(date));
}

function authorToMessage(
  history: History,
  displayAuthor: boolean = true,
  getUser: boolean = true,
) {
  return displayAuthor &&
    ((getUser && history.user) || (!getUser && history.signatory))
    ? " - " +
        (getUser
          ? `${formatName(history.user.firstname)} ${formatName(
              history.user.lastname,
            )}`
          : history.signatory)
    : "";
}

function getMessageStatus(
  transaction: TransactionDetail,
): string | JSX.Element | JSX.Element[] {
  if (!transaction) {
    return "-";
  }
  if (transaction.classified) {
    const transactionStatusTranslation = `transaction.status.${transaction.status}`;
    const transactionStatus = `${i18n.t("transaction.status.classified")} <i>(${
      i18n.exists(transactionStatusTranslation)
        ? i18n.t(transactionStatusTranslation)
        : ""
    })</i>`;
    const transactionExtraStatus = `${
      transaction.status === "canceled" || transaction.status === "refused"
        ? transaction.cause && transaction.cause.replace(/\s/g, "").length > 0
          ? ` | ${i18n.t("transaction.cause")}` + transaction.cause
          : ""
        : ""
    }`;

    return parse(
      `${transactionStatus}${transactionExtraStatus != "" ? `(${transactionExtraStatus})` : ""}`,
    );
  } else if (
    transaction.status === "finished" &&
    transaction.manuallyFinalized
  ) {
    return i18n.t("transaction.status.finishedFinalized");
  } else {
    return i18n.t("transaction.status." + transaction.status);
  }
}

interface TransactionTitleSectionProps {
  transaction: TransactionDetail;
  histories: History[];
  className?: string;
}

const TransactionTitleSection: FunctionComponent<
  TransactionTitleSectionProps
> = ({ transaction, histories, className = "" }) => {
  const { t } = useTranslation();

  const historyPrepared = histories.filter(
    (history) => history.status === "prepared",
  )[0];
  const historyInProgress = histories.filter(
    (history) => history.status === "inProgress",
  )[0];
  const historyCanceled = histories.filter(
    (history) => history.status === "canceled",
  )[0];
  const historyFinished = histories.filter(
    (history) => history.status === "finished",
  )[0];
  const historyClassified = histories.filter(
    (history) => history.status === "classified",
  )[0];
  const historyRefused = histories.filter(
    (history) => history.status === "refused",
  )[0];
  const errorHistories = histories.filter(
    (history) => history.status === "error",
  );
  const historyExpired = histories.filter(
    (history) => history.status === "expired",
  );
  const totalNumberSignatories = transaction
    ? transaction.signatories?.length
    : 1;
  const signedNumberSignatories = transaction
    ? transaction.signatories?.filter(
        (signatory) => signatory.status === "signed",
      ).length
    : 1;
  const showProgession =
    transaction &&
    transaction.type === "signature" &&
    transaction.status != undefined &&
    !showProgressionList.includes(transaction.status) &&
    !transaction.manuallyFinalized;

  const isCanceledOrRejectedOrPending: boolean =
    !!transaction.status &&
    ["canceled", "refused", "pending"].includes(transaction.status);

  const isManuallyFinalized = transaction.manuallyFinalized;
  const isClassified = transaction.classified;
  const hasValidCause =
    transaction.cause && transaction.cause?.replace(/\s/g, "").length > 0;

  const shouldDisplayMotif =
    transaction.status &&
    (isCanceledOrRejectedOrPending || isManuallyFinalized) &&
    !isClassified &&
    (hasValidCause || isManuallyFinalized);

  return (
    <div
      className={`app-section-transaction-title border-bottom-dark-grey p-24 ${className}`}
    >
      <Row gutter={30}>
        <Col span={24} md={12}>
          <DetailItem
            title={t("transaction.detail.title.title")}
            description={transaction.object ?? ""}
            dataTest={transaction.object}
            descriptionBold
            index={0}
          />
          {transaction && transaction.status !== "draft" && (
            <div
              data-test={"input-label"}
              data-alt={t("transaction.detail.title.preparationDate")}
            >
              <DetailItem
                title={t("transaction.detail.title.preparationDate")}
                description={historyToMessage(historyPrepared)}
                index={2}
              />
            </div>
          )}
          {transaction &&
            transaction.status !== "prepared" &&
            transaction.status !== "draft" && (
              <div
                data-test={"input-label"}
                data-alt={`${t("transaction.detail.title.sendFor")}${
                  transaction ? transaction.type : ""
                } :`}
              >
                <DetailItem
                  title={`${t("transaction.detail.title.sendFor")}${
                    transaction ? transaction.type : ""
                  } :`}
                  tooltipLabel={historyToMessage(
                    historyInProgress,
                    true,
                    true,
                    errorHistories,
                    historyExpired,
                  )}
                  description={
                    errorHistories && errorHistories.length > 0
                      ? historyToMessage(
                          errorHistories[errorHistories.length - 1],
                        )
                      : historyExpired && historyExpired.length > 0
                        ? historyToMessage(
                            historyExpired[historyExpired.length - 1],
                          )
                        : historyToMessage(historyInProgress)
                  }
                  index={3}
                />
              </div>
            )}
        </Col>
        <Col span={24} md={12}>
          <div
            data-test={"input-label"}
            data-alt={t("transaction.detail.title.status")}
          >
            <DetailItem
              bgColor={getBackgroundColor(
                transaction.status,
                transaction.classified,
              )}
              title={t("transaction.detail.title.status")}
              icon={getIcon(transaction.status ?? "", transaction.classified)}
              textColor={getTextColor(
                transaction.status,
                transaction.classified,
              )}
              description={getMessageStatus(transaction)}
              index={4}
            />
          </div>
          {transaction.status && shouldDisplayMotif && (
            <div
              data-test={"input-label"}
              data-alt={t("transaction.detail.title.reason")}
            >
              <DetailItem
                bgColor={getBackgroundColor(
                  transaction.status,
                  transaction.classified,
                )}
                title={t("transaction.detail.title.reason")}
                textColor={getTextColor(
                  transaction.status,
                  transaction.classified,
                )}
                description={
                  transaction.cause ||
                  t("transaction.detail.title.defaultReason")
                }
                index={5}
              />
            </div>
          )}
          {transaction && transaction.status === "canceled" && (
            <div
              data-test={"input-label"}
              data-alt={t("transaction.detail.title.cancel")}
            >
              <DetailItem
                bgColor={getBackgroundColor(
                  transaction.status,
                  transaction.classified,
                )}
                title={t("transaction.detail.title.cancel")}
                textColor={getTextColor(
                  transaction.status,
                  transaction.classified,
                )}
                description={historyToMessage(historyCanceled)}
                index={6}
              />
            </div>
          )}
          {transaction && transaction.status === "finished" && (
            <div
              data-test={"input-label"}
              data-alt={t("transaction.detail.title.endDate")}
            >
              <DetailItem
                bgColor={getBackgroundColor(
                  transaction.status,
                  transaction.classified,
                )}
                title={t("transaction.detail.title.endDate")}
                textColor={getTextColor(
                  transaction.status,
                  transaction.classified,
                )}
                description={
                  historyFinished
                    ? historyToMessage(historyFinished, false)
                    : historyRefused
                      ? historyToMessage(historyRefused, false)
                      : historyToMessage(historyCanceled, false)
                }
                index={7}
              />
            </div>
          )}
          {transaction && transaction.status === "inProgress" && (
            <div
              data-test={"input-label"}
              data-alt={t("transaction.detail.title.expirationDate")}
            >
              <DetailItem
                bgColor={getBackgroundColor(transaction.status)}
                title={t("transaction.detail.title.expirationDate")}
                textColor={getTextColor(transaction.status)}
                description={dateToMessage(transaction.expirationDate ?? "")}
                index={8}
              />
            </div>
          )}
          {transaction && transaction.status === "refused" && (
            <div
              data-test={"input-label"}
              data-alt={t("transaction.detail.title.rejectionDate")}
            >
              <DetailItem
                bgColor={getBackgroundColor(
                  transaction.status,
                  transaction.classified,
                )}
                title={t("transaction.detail.title.rejectionDate")}
                textColor={getTextColor(
                  transaction.status,
                  transaction.classified,
                )}
                description={historyToMessage(historyRefused, true, false)}
                index={9}
              />
            </div>
          )}
          {transaction && transaction.classified && (
            <div
              data-test={"input-label"}
              data-alt={t("transaction.detail.title.classificationDate")}
            >
              <DetailItem
                bgColor={getBackgroundColor(
                  transaction.status,
                  transaction.classified,
                )}
                title={t("transaction.detail.title.classificationDate")}
                textColor={getTextColor(
                  transaction.status,
                  transaction.classified,
                )}
                description={historyToMessage(historyClassified)}
                index={10}
              />
            </div>
          )}
          {showProgession && (
            <div
              data-test={"progress-field"}
              data-alt={t("transaction.detail.title.progression").trim()}
            >
              <DetailItem
                bgColor={getBackgroundColor(
                  transaction.status,
                  transaction.classified,
                )}
                title={t("transaction.detail.title.progression")}
                textColor={getTextColor(
                  transaction.status,
                  transaction.classified,
                )}
                description={
                  <BasicProgress
                    percent={
                      signedNumberSignatories && totalNumberSignatories
                        ? (signedNumberSignatories /
                            (transaction.manuallyFinalized
                              ? signedNumberSignatories
                              : totalNumberSignatories)) *
                          100
                        : 0
                    }
                    label={`${signedNumberSignatories}/${totalNumberSignatories}`}
                  />
                }
                index={11}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default TransactionTitleSection;
